import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from '../common/Button';
import { useToast } from '../common/Toast';

interface FeedbackRatingCardProps {
  interviewId: string;
  currentRating?: string;
  onRatingSubmit: (rating: 'like' | 'dislike') => Promise<void>;
  className?: string;
}

export const FeedbackRatingCard: React.FC<FeedbackRatingCardProps> = ({
  interviewId,
  currentRating,
  onRatingSubmit,
  className = ''
}) => {
  const [selectedRating, setSelectedRating] = useState<string | undefined>(currentRating);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  const handleRatingClick = async (rating: 'like' | 'dislike') => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      await onRatingSubmit(rating);
      setSelectedRating(rating);
      showToast('Thank you for your feedback!', 'success');
    } catch (error) {
      console.error('Error submitting feedback rating:', error);
      showToast('Failed to submit feedback. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isRated = selectedRating !== undefined;

  return (
    <div className={`bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30 ${className}`}>
      <h3 className="text-lg font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
        {isRated ? 'Feedback Submitted!' : 'Did this feedback help?'}
      </h3>
      
      {!isRated || !selectedRating || selectedRating === null ? (
        <div className="space-y-4">
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
            Let us know if you find the interview feedback accurate & helpful for improving your performance.
          </p>
          
          <div className="flex flex-row w-full gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleRatingClick('like')}
              disabled={isSubmitting}
              leftIcon={<ThumbsUp size={16} className="text-primary-light dark:text-primary-dark" />}
              className="w-[48%]"
            >
             Yes
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleRatingClick('dislike')}
              disabled={isSubmitting}
              leftIcon={<ThumbsDown size={16} className="text-red-500" />}
              className="w-[52%] flex-1 flex items-center justify-center gap-2 hover:text-red-500 hover:border-red-300 dark:hover:border-red-700"
            >

              <span className='group-hover:text-red-500'>No</span>
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
            Thank you for your feedback. We'll use this to improve our interview feedback system.
          </p>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedRating(undefined)}
            className="text-sm"
          >
            Change Feedback
          </Button>
        </div>
      )}
    </div>
  );
};

export default FeedbackRatingCard; 